<template>
  <p
    v-if="isLoading"
    class="memorial-form__desc skeleton-loader"
    style="height: 3.8em"
  />
  <p v-else-if="!order || !order.memorial" />
  <p v-else class="memorial-form__desc" v-html="memorialText" />
</template>

<script>
import { sprintf } from 'sprintf-js';
import { parse } from 'date-fns';
import { mapState } from 'vuex';

export default {
  name: 'RsvpDescription',

  data() {
    const { dataset } = this.$mountEl;

    return {
      id: dataset.orderId,
      rsvp: dataset.rsvp,
      startDate: dataset.startDate,
      endDate: dataset.endDate,
      isAppRsvp: dataset.appRsvp === '1',
      texts: lavendlaObituary.rsvp,
      locale: theme_vars.locale.replace('_', '-'),
    };
  },

  computed: {
    ...mapState('orders', ['order', 'isLoading']),

    // Prefer API data for dates, fall back to HTML dataset
    effectiveStartDate() {
      return this.order?.memorial?.start_date || this.startDate;
    },

    effectiveEndDate() {
      return this.order?.memorial?.end_date || this.endDate;
    },

    hasMaxAttendees() {
      return this.order.memorial.maxAttendees > 0;
    },

    rsvpDate() {
      if (this.rsvp) {
        return this.parse(this.rsvp, 'yyyy-MM-dd', new Date());
      }

      // If no startDate is set, fall back to today's date
      if (!this.effectiveStartDate) {
        return new Date();
      }

      const date = this.parse(
        this.effectiveStartDate,
        'yyyy-MM-dd H:m:s',
        new Date()
      );
      date.setDate(date.getDate() - 1);

      return date;
    },

    canRSVP() {
      if (this.isLoading) {
        return false;
      }

      const today = new Date();
      today.setHours(0, 0, 0, 0); // make sure we dont compare time.

      return this.rsvpDate >= today;
    },

    hasSpotsLeft() {
      if (this.isLoading) {
        return false;
      }

      if (!this.hasMaxAttendees) {
        return true;
      }

      return (
        this.order.memorial.maxAttendees > this.order.memorial.numAttendees
      );
    },

    memorialDateTime() {
      if (this.isLoading) {
        return null;
      }

      // Return null if no startDate is set
      if (!this.effectiveStartDate) {
        return null;
      }

      // Check if times should be hidden via the toggle (backwards compatible with hide_end_date)
      const hideTimes =
        this.order.memorial.hide_times ??
        this.order.memorial.hide_end_date ??
        false;
      if (hideTimes) {
        return null;
      }

      const startDate = this.parse(
        this.effectiveStartDate,
        'yyyy-MM-dd H:m:s',
        new Date()
      );
      const isStartDateOnly = this.isDateOnly(startDate);

      // If no end date, use start date or show date only
      if (!this.effectiveEndDate) {
        return isStartDateOnly
          ? this.formatDateOnly(startDate)
          : this.formatDateWithTime(startDate);
      }

      const endDate = this.parse(
        this.effectiveEndDate,
        'yyyy-MM-dd H:m:s',
        new Date()
      );
      const isEndDateOnly = this.isDateOnly(endDate);

      // Both dates are date-only (00:00:00)
      if (isStartDateOnly && isEndDateOnly) {
        return this.formatDateOnly(startDate);
      }

      return `${this.formatDateWithTime(startDate)} - ${this.formatTimeOnly(endDate)}`;
    },

    memorialPlaceText() {
      return !this.order.memorial.place
        ? ''
        : sprintf(this.texts.memorialPlaceText, this.order.memorial.place);
    },

    memorialText() {
      if (this.isLoading || !this.order?.memorial) {
        return;
      }

      const formattedRsvpDate = `<strong>${this.formatShortDate(this.rsvpDate)}</strong>`;

      if (this.hasSpotsLeft && this.canRSVP) {
        // The app sign-up panel shows the deadline, so the invitation leaves it out
        if (this.isAppRsvp) {
          return this.memorialDateTime
            ? sprintf(
                this.texts.appRsvpInvitation,
                this.memorialDateTime,
                this.memorialPlaceText
              )
            : sprintf(this.texts.appRsvpInvitationNoTime, this.memorialPlaceText);
        }

        // Use different translation if no time is set or times are hidden
        if (!this.memorialDateTime) {
          return sprintf(
            this.texts.advisorHandlesRSVPNoTime,
            this.memorialPlaceText,
            formattedRsvpDate
          );
        }
        return sprintf(
          this.texts.advisorHandlesRSVP,
          this.memorialDateTime,
          this.memorialPlaceText,
          formattedRsvpDate
        );
      }

      if (this.canRSVP) {
        return this.texts.noSpotsLeft;
      }

      return this.texts.rsvpDateHasPassed;
    },
  },

  created() {
    if (this.id && !this.order) {
      this.$store.dispatch('orders/getOrder', this.id);
    }
  },

  methods: {
    parse,

    /**
     * Format a date as "weekday, month day" (e.g., "Monday, February 20")
     */
    formatDateOnly(date) {
      return date.toLocaleDateString(this.locale, {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
      });
    },

    /**
     * Format a date with time as "weekday, month day, HH:mm" (e.g., "Monday, February 20, 14:00")
     */
    formatDateWithTime(date) {
      return date.toLocaleDateString(this.locale, {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    },

    /**
     * Format time only as "HH:mm" (e.g., "14:00")
     */
    formatTimeOnly(date) {
      return date.toLocaleTimeString(this.locale, {
        hour: '2-digit',
        minute: '2-digit',
      });
    },

    /**
     * Format a short date as "month day" (e.g., "February 20")
     */
    formatShortDate(date) {
      return date.toLocaleDateString(this.locale, {
        month: 'long',
        day: 'numeric',
      });
    },

    /**
     * Check if a date has no time component (00:00:00)
     */
    isDateOnly(date) {
      return date.getHours() === 0 && date.getMinutes() === 0;
    },
  },
};
</script>
